import { EXCEPTION_KEYWORD } from '../constants/common';
import env from './env';

/** kc-helper(`kc-helper-services`)와 분리된 프리뷰 전용 localStorage 키 */
const STORAGE_KEY = 'kc-preview-services';
/** 프리뷰 오버라이드가 허용되는 호스트네임 접두어 */
const PREVIEW_HOST_PREFIX = EXCEPTION_KEYWORD.PREVIEW_CONSOLE;
/** PR 번호는 URL 경로 세그먼트로 삽입되므로 숫자만 허용 */
const PR_NUMBER_PATTERN = /^\d{1,7}$/;
const SERVICE_NAME_PATTERN = /^[a-z0-9-]+$/;

const PREVIEW_SERVICES_PARAM = 'preview_services';
const PREVIEW_PR_PARAM = 'preview_pr';

interface PreviewServiceOverride {
  name: string;
  version: string;
}

/**
 * 프리뷰 버전 오버라이드가 허용되는 환경인지 확인합니다.
 * @description
 * - 환경변수(`KC_ENV === 'stage'`)와 도메인(hostname이 `preview-console`로 시작) 이중 게이팅을 적용합니다.
 * - 저장(브릿지)과 조회(getPreviewVersion) 양쪽에 동일하게 적용되어,
 *   localStorage가 잔존해도 프리뷰 도메인 밖에서는 오버라이드가 발동하지 않습니다.
 */
export const isPreviewHost = () => {
  return (
    typeof window !== 'undefined' &&
    env.KC_ENV === 'stage' &&
    window.location.hostname.startsWith(PREVIEW_HOST_PREFIX)
  );
};

const readPreviewOverrides = (): PreviewServiceOverride[] => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];

  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is PreviewServiceOverride =>
        typeof item?.name === 'string' && typeof item?.version === 'string',
    );
  } catch {
    return [];
  }
};

/**
 * URL 쿼리(`preview_services`, `preview_pr`)를 읽어 프리뷰 버전 오버라이드를 localStorage에 저장합니다.
 * @description
 * - PR 프리뷰 링크(`?preview_services=vpc,dns&preview_pr=4567`) 진입 시 1회 실행되는 브릿지입니다.
 *   내부 네비게이션에서 쿼리파라미터가 유실되므로, 최초 진입 시점에 localStorage로 옮겨 유지합니다.
 * - 저장 포맷은 `[{ name, version: 'pr/{PR번호}' }]`이며 기존 오버라이드와 병합됩니다(동일 서비스는 교체).
 * - 처리한 파라미터는 URL에서 제거하고 나머지 쿼리는 보존합니다.
 * - 렌더 이후(effect)에는 CSR 로직이 쿼리를 제거할 수 있으므로, 첫 렌더 전에 동기 호출해야 합니다.
 */
export const applyPreviewOverrideFromQuery = () => {
  if (!isPreviewHost()) return;

  // determineProjectGuard의 이중 `?` 리다이렉트로 첫 파라미터 키가 `?foo`로 오염될 수 있어 선행 `?`를 모두 제거 후 파싱합니다.
  const params = new URLSearchParams(window.location.search.replace(/^\?+/, ''));
  const services = params.get(PREVIEW_SERVICES_PARAM);
  const prNumber = params.get(PREVIEW_PR_PARAM);
  if (!services || !prNumber || !PR_NUMBER_PATTERN.test(prNumber)) return;

  const overrides = services
    .split(',')
    .map(name => name.trim())
    .filter(name => SERVICE_NAME_PATTERN.test(name))
    .map(name => ({ name, version: `pr/${prNumber}` }));
  if (overrides.length === 0) return;

  const rest = readPreviewOverrides().filter(
    prev => !overrides.some(override => override.name === prev.name),
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...rest, ...overrides]));

  params.delete(PREVIEW_SERVICES_PARAM);
  params.delete(PREVIEW_PR_PARAM);
  const search = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
};

/**
 * 서비스의 프리뷰 오버라이드 버전을 조회합니다.
 * @param serviceName 버전을 오버라이드할 서비스 이름입니다.
 * @returns 오버라이드된 버전 문자열(`pr/{PR번호}`). 프리뷰 환경이 아니거나 설정이 없으면 undefined.
 */
export const getPreviewVersion = (serviceName: string) => {
  if (!isPreviewHost()) return;

  return readPreviewOverrides().find(service => service.name === serviceName)?.version;
};

/**
 * 프리뷰 버전 오버라이드를 모두 해제합니다.
 * @description 프리뷰 상태 배너의 해제 버튼 등에서 사용합니다.
 */
export const clearPreviewOverrides = () => {
  localStorage.removeItem(STORAGE_KEY);
};
